import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _NavigationService;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ContinueRouteChangeType, RouteChangeContext, RouteChangeType } from "abreact/incubator/ui/app/router/RouteChangeContext";
import { UnmountRouteEvent } from "abreact/incubator/ui/app/router/RouteEvent";
import { PrimaryRouteRenderContext } from "abreact/incubator/ui/app/router/routeRenderContext/PrimaryRouteRenderContext";
import { RouteRenderContextManager } from "abreact/incubator/ui/app/router/routeRenderContext/RouteRenderContextManager";
import { isAbSDKRegisteredRoute } from "abreact/incubator/ui/app/router/simple/AbSDKRegisteredRoute";
import { HOME_ALIAS_ROUTE } from "abreact/incubator/ui/app/router/simple/RootAliasRoute";
import { RouteRegistryImpl } from "abreact/incubator/ui/app/router/simple/RouteRegistryImpl";
import * as AwSnapRoute from "abreact/incubator/ui/app/routes/AwSnapRoute";
import * as EmptyRoute from "abreact/incubator/ui/app/routes/EmptyRoute";
import { unauthorizedRouteSpec } from "abreact/incubator/ui/app/routes/UnauthorizedRouteSpec";
import { unknownRouteSpec } from "abreact/incubator/ui/app/routes/UnknownRouteSpec";
import * as UnmountRoute from "abreact/incubator/ui/app/routes/UnmountRoute";
import { ReactErrorRenderer } from "abreact/incubator/ui/error/ReactErrorRenderer";
import { NestedError } from "abweb/dto/exception/NestedError";
import { AbWebServices } from "abweb/service/AbWebServices";
import { Assert } from "abweb/utils/Assert";
import { BrowserUtils } from "abweb/utils/BrowserUtils";
import { AsyncCallback1Arg } from "abweb/utils/callback/AsyncCallback1Arg";
import { DebugUtils } from "abweb/utils/DebugUtils";
import { ErrorContext } from "abweb/utils/error/ErrorContext";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { observable } from "mobx";
const LOG = LogUtils.getLogger(__filename);
/**
 * <p>This class is responsible for setting up the routing table so that when the browser navigates to an
 * arbitrary url, the corresponding view is displayed in a designated route render context (e.g., mainArea).
 * </p>
 * <p>Note that there can be ONLY ONE navigation service per application as there is only one browser url.
 * </p>
 */
export class NavigationService {
  /**
   * Application code should NOT use this weakly typed singleton.
   * Instead, typically use a strongly typed app-specific accessor of the instantiated NavigationService,
   * like WorkbenchAppServices.NavigationService.
   * @Deprecated will be removed
   */
  // Needed to ensure that vanilla lib widgets that need to access either navService, changeView, currentRouteHandler
  // can do so without having access to the application facade that holds the (app-specific) strongly typed NavService.
  // Can NOT return strongly typed version as generic class args aren't available in static scope
  static getSingleton() {
    if (!NavigationService.hasNavigationService()) {
      throw new Error(`Unable to access NavigationService as it has not been initialized!`);
    }
    return NavigationService.SINGLETON.get();
  }
  static hasNavigationService() {
    return NavigationService.SINGLETON.get() !== undefined;
  }

  /** Called by test infrastructure */
  static resetStatics() {
    LOG.debug("Calling resetStatics");
    if (NavigationService.hasNavigationService()) {
      const navSvc = NavigationService.getSingleton();
      navSvc.destroy();
      NavigationService.SINGLETON.set(undefined);
    }
  }
  /**
   * Creates a navigation service.  It also registers a 'teardown' callback that will prevent navigating away
   * from the app if the current route has unsaved changes.
   *
   * @param {Map<string, RegisterRoutesT extends IRegisterRoutes>} regRoutesRegistry
   * (routeCallbacks can also invoke private goto methods).  Note that there can be ONLY ONE navigation service
   * per application as there is only one browser url.  Deprecated for absdk router.
   * @param {NavigationServiceOptions} options contains optional configuration
   */
  constructor(regRoutesRegistry, options) {
    _defineProperty(this, "instanceId", NavigationService.INSTANCE_ID++);
    // useful for debugging to ensure navSvc identity
    _defineProperty(this, "router", AbWebServices.router);
    /**
     * Map of the Route Handler factories by RouteTemplate.
     * Use getRegisteredRoute method to retrieve RegisteredRoute for a given routeTemplate, which
     * will throw an error if there is no RegisteredRoute for the given routeTemplate.
     * Do NOT modify this map!
     */
    _defineProperty(this, "routeHandlerFactoryByRouteTemplate", new Map());
    /**
     * Contains mainArea routeRenderContext, which holds the currently mounted mainArea route.
     * Note that the mainArea is directly tied to the browser's url and back/forward navigation history buttons.
     */
    _defineProperty(this, "mainAreaRouteRenderContext", void 0);
    _defineProperty(this, "routeRenderContextMgr", new RouteRenderContextManager());
    /** Lookup registeredRoute (absdk) by routeTemplate. Not available for non-absdk routes */
    _defineProperty(this, "registeredAbSDKRouteByRouteTemplate", new Map());
    _defineProperty(this, "awSnapRoute", void 0);
    // used internally
    _defineProperty(this, "emptyRoute", void 0);
    // used by cypress tests
    _defineProperty(this, "unmountRegRoute", void 0);
    // used by cypress tests
    _defineProperty(this, "suppressFrozenRoutes", void 0);
    _defineProperty(this, "defaultHomeRoute", void 0);
    _defineProperty(this, "homeRoute", void 0);
    _defineProperty(this, "unknownRoute", void 0);
    _defineProperty(this, "unauthorizedRoute", void 0);
    _defineProperty(this, "routeEventListeners", new AsyncCallback1Arg());
    this.regRoutesRegistry = regRoutesRegistry;
    if (NavigationService.hasNavigationService()) {
      throw new Error("Already have a NavigationService, please invoke resetStatics between tests");
    }
    this.defaultHomeRoute = options?.defaultHomeRoute;
    this.suppressFrozenRoutes = options?.suppressFrozenRoutes ?? false;
    if (this.defaultHomeRoute?.routeSpec.routeTemplate === HOME_ALIAS_ROUTE.routeTemplate) {
      throw new Error("The defaultHomeRoute can not have a routeTemplate of '/', as that is reserved for the RootAliasRoot");
    }
    this.mainAreaRouteRenderContext = PrimaryRouteRenderContext.SINGLETON;
    this.routeRenderContextMgr.registerRouteRenderContext(this.mainAreaRouteRenderContext);
    this.router.registerContinueRouteChangeListener(this.continueRouteChangeListener.bind(this));
    const [builtinRoutesRegistry, unknownRegRoute, unauthorizedRoute] = this.registerBuiltinRoutes(options?.unknownRoute, options?.unauthorizedRoute);
    this.unauthorizedRoute = unauthorizedRoute;
    this.unknownRoute = unknownRegRoute;
    this.router.setBuildUnknownRouteUrlFn(unknownRoute => unknownRegRoute.href({
      route: unknownRoute
    }).substring(1) // remove leading '#'
    );
    const builtinRawRoutes = builtinRoutesRegistry.registerRoutesByRouteTemplate.values();
    this.registerRoutes([...builtinRawRoutes, ...this.regRoutesRegistry.values()]);
    this.awSnapRoute = this.getRegisteredRouteOrError(AwSnapRoute.AWSNAP_ROUTE_TEMPLATE);
    this.emptyRoute = this.getRegisteredRouteOrError(EmptyRoute.EMPTY_ROUTE_TEMPLATE);
    this.unmountRegRoute = this.getRegisteredRouteOrError(UnmountRoute.UNMOUNT_ROUTE_TEMPLATE);
    this.homeRoute = this.defaultHomeRoute?.buildRouteFromProps({}) ?? HOME_ALIAS_ROUTE;
    this.registerPreventAppUnloadFn();
    AbWebServices.builtinRouteProvider = this; // RTD-backed implementation of the gotoHome and gotoLogout routes

    // Do LAST so observers (e.g., AbRouteMainAreaRender) get a fully initialized Nav Service instance
    NavigationService.SINGLETON.set(this);
  }
  destroy() {
    this.routeEventListeners.removeAllCallbacks();
    this.routeRenderContextMgr.destroy();
  }
  registerPreventAppUnloadFn() {
    // Browser-initiated url changes (url edits, bookmarks, back/forward buttons)
    // will display builtin-dialog saying "Reload site?  Changes you made may not be saved".  Dialog
    // differs per browser vendor.  They do NOT let the app display their own dialogs as malicious
    // websites can then trap users by vetoing any navigation away from the website.
    const preventAppUnloadFn = () => {
      const dirtyRCCs = this.routeRenderContextMgr.getDirtyRouteRenderContexts();
      return dirtyRCCs.length > 0;
    };
    AbWebServices.teardownService().preventAppUnloadVotersRegistration.addCallback(preventAppUnloadFn);
  }

  /**
   * @returns Typed currentRouteHandler for mainArea.  Note that there are some routes
   *   (e.g., built-in AwSnap route) that will be different from the app's base route type, so downcast judiciously
   */
  get currentMainAreaRouteHandler() {
    return this.mainAreaRouteRenderContext.currentRouteHandler;
  }

  // NOTE: Invoked ONLY for BROWSER NAVIGATION EVENTS generated by links, fore/back buttons, URL edits, etc,
  // and these browser-initiated route changes apply to the mainArea route render context.
  // Note browser has ALREADY bookmarked previous route and changed current url to new route
  // It is NOT invoked on url route updates initiated by app.
  continueRouteChangeListener(continueRouteChangeContext) {
    const {
      newRoute,
      historyInitiatedFn,
      afterRevertP
    } = continueRouteChangeContext;
    LOG.info("Changing Route VIA BROWSER NAVIGATION to: " + newRoute.route);
    const newRegRoute = this.getRegisteredRouteOrError(newRoute.routeTemplate);
    const newReactRoute = newRegRoute.buildRouteFromParams(newRoute);
    // ABSDK-2874: do not bookmark current url as browser auto-bookmarks browser-initiated route changes.
    const routeChangeContext = this.createRouteChangeContext(newReactRoute, this.mainAreaRouteRenderContext, false, true, historyInitiatedFn);
    return this.continueRouteChangeBrowser(routeChangeContext, afterRevertP);
  }

  /**
   * @return true to continue browser-initiated route change
   * Note: if an async veto workflow is needed, continueRouteChangeBrowser returns false
   * (cancelling the browser initiated route change), and launch new 'goto' changeView which
   * can handle the async veto workflow.
   */
  continueRouteChangeBrowser(routeChangeContext, afterRevertP) {
    const handleBrowserRouteChangeErrorFn = (routeChangeContext, err) => {
      // CAT-1240: last chance: treat veto exceptions as 'continue to new route anyway' as current route is probably hosed
      this.logAndRenderError(err).then(() => this.changeView(this.buildBrowserRouteChangeContextContinuation(routeChangeContext)));
    };
    const currRH = routeChangeContext.routeRenderContext.currentRouteHandler;
    let continueRC = false;
    let retryRC = false;
    try {
      // assign to existing vars using destructuring, note outer parens!
      ({
        continueRC,
        retryRC
      } = this.continueRouteChangeSync(currRH, routeChangeContext));
    } catch (err) {
      handleBrowserRouteChangeErrorFn(routeChangeContext, err);
      // Although below retVal is cancelling current route change call stack,
      // the handleContinueRouteChangeError above will async change to new route
      // after displaying the error!
      return false;
    }
    if (!continueRC && retryRC) {
      // Subtle: this 'sync' continueRouteChangeBrowser call will return false, so this browser initiated route change
      // will be aborted.  It will be later be 'continued' via a new changeView call if a user saves/discards in
      // the retryRouteChange
      afterRevertP.then(() => currRH.retryRouteChange(routeChangeContext)).then(continueRouteChange => {
        if (continueRouteChange) {
          // Invoke Continuation workflow on successful Save/Discard in veto dialog

          // If forward/back button initiated, retry via orig history function so that history is correct.
          if (routeChangeContext.historyInitiatedFn) {
            routeChangeContext.historyInitiatedFn();
            // Note that original changeView initiated via back|forward button, so there is NO caller awaiting the
            // results of the resulting changeView.  As such, just return false indicating CURRENT route change
            // failed (but subsequent retry via back() above may succeed!)
            return false;
          }
          // Else treat as 'forward route change' so current route will go on 'back history'
          return this.changeView(this.buildBrowserRouteChangeContextContinuation(routeChangeContext));
        }
        // no continuation, so user hit 'Cancel' in veto dialog
      },
      // catch below only handles retryRouteChange exceptions.
      // The then-block's above changeView is expected to handle its own exceptions
      err => handleBrowserRouteChangeErrorFn(routeChangeContext, err));
    }
    return continueRC;
  }

  /**
   * Common logic for both browser and code (aka goto) initiated route changes to
   * invoke currRH.currentRouteChange.  It is up to the caller to handle any thrown errors,
   * as handling differs for sync (browser) and async (goto) call stacks
   */
  continueRouteChangeSync(currRH, routeChangeContext) {
    if (!currRH) {
      return {
        continueRC: true,
        retryRC: false
      };
    }
    const continueRouteChangeRetVal = currRH.continueRouteChange(routeChangeContext);
    return this.processRouteChangeType(continueRouteChangeRetVal);
  }
  processRouteChangeType(continueRouteChange) {
    let continueRC = false;
    let retryRC = false;
    switch (continueRouteChange) {
      case ContinueRouteChangeType.CONTINUE:
        // case true:
        continueRC = true;
        retryRC = false;
        break;
      case ContinueRouteChangeType.CANCEL_AND_RETRY:
        // case false:
        continueRC = false;
        retryRC = true;
        break;
      case ContinueRouteChangeType.CANCEL:
        continueRC = false;
        retryRC = false;
        break;
      default:
        Assert.assertNever(continueRouteChange);
    }
    return {
      continueRC,
      retryRC
    };
  }
  async continueRouteChangeAsync(routeChangeContext) {
    const currRH = routeChangeContext.routeRenderContext.currentRouteHandler;
    try {
      let {
        continueRC,
        retryRC
      } = this.continueRouteChangeSync(currRH, routeChangeContext);
      if (!continueRC && retryRC) {
        continueRC = await currRH.retryRouteChange(routeChangeContext);
      }
      return continueRC;
    } catch (err) {
      // Kicks off another top-level attempt to changeView that bypasses veto reasoning
      await this.logAndRenderError(err);
      // Showed them error from continue/retry workflow, now just continue to new route anyway.
      // Any edits in currentRH will be lost!
      return true;
    }
  }
  buildBrowserRouteChangeContextContinuation(routeChangeContext) {
    // Known 'history is wonky' issue for route continuation:
    // 1. User hits Back button.  By the time get here, the url will already be the new url, so the currentUrl is
    //    inaccessible (and thus can't be saved to History by the continuation changeView).
    // 2. Secondary: if user hit 'Back' button, then browser puts history on 'Forward' history button (assuming not cancelled)
    // However, continuation always saves history in 'Back' button history, so there is a disconnect.
    // To fix, would have to thread through the '_back' prop that is currently
    // removed by KendoRouter.ts::fixupExtraParamsCallback method
    // noinspection JSIgnoredPromiseFromCall
    return {
      newRoute: routeChangeContext.newReactRoute,
      routeRenderContext: routeChangeContext.routeRenderContext,
      // kendo 'browser initiated' route changes always addCurrentRouteToBrowserHistory, and this changeView 'continuation'
      // is a 'logical continuation' of the browser initiated change (original browser route change was aborted!)
      addCurrentRouteToBrowserHistory: true,
      browserInitiated: true /* setting browserInitiated=true causes veto processing to be bypassed */
    };
  }

  // Log and render error, and after error dialog is closed it will invoke the onComplete function, whose implementation
  // typically navigates to another page
  logAndRenderError(errUntyped) {
    let resolveFn, rejectFn;
    const p = new Promise((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });
    try {
      LogAndRenderErrorPipeline.pipeline.logError(errUntyped); // debounces, logs error

      // For now, bypass pipeline.renderHandler as there is a chance that it is not a ReactErrorRenderer
      // (which supports the optional onComplete arg).
      // Longer-term: thread onComplete through pipeline method args, or add 'onRenderComplete' to errCtx?
      const errCtx = ErrorContext.createFromError(errUntyped);
      LogAndRenderErrorPipeline.pipeline.setLastErrorForDebounce(errUntyped);
      new ReactErrorRenderer().handleErrorContext(errCtx, () => resolveFn());
    } catch (err) {
      rejectFn(err);
    }
    return p; // will be unresolved until either resolveFn or rejectFn are sync invoked by above workflow
  }
  registerRoutes(registerRoutes) {
    const duplicateRouteTemplates = new Set();
    registerRoutes.forEach(rr => {
      // Ensure that if any of the routeTemplates collide with existing registered route templates,
      // the registration is skipped entirely for that rr.
      const routeTemplate = rr.getRouteInfo().routeTemplate;
      const dupRegRoute = this.getRegisteredRoute(routeTemplate);
      if (!dupRegRoute) {
        this.registerRoute(rr);
      } else {
        duplicateRouteTemplates.add(routeTemplate);
      }
    });
    if (duplicateRouteTemplates.size > 0) {
      const dupRTs = `['${[...duplicateRouteTemplates].join("', '")}']`;
      const errMsg = AbReactResources.navigationService_duplicateRoute_detail(dupRTs);
      if (DebugUtils.THROW_ON_DEV_ERROR) LogAndRenderErrorPipeline.pipeline.reportError(new Error(errMsg));else LOG.error(errMsg);
    }
  }
  unregisterRoutes(routeTemplates) {
    routeTemplates.forEach(routeTemplate => {
      const regRoute = this.getRegisteredRoute(routeTemplate);
      if (!regRoute) {
        LOG.warn(`Can not unregister route ${routeTemplate} as it is not registered.`);
      } else {
        this.unregisterRoute(regRoute);
      }
    });
  }
  registerBuiltinRoutes(unknownRoute, unauthorizedRoute) {
    // builtin routes: jam into registry even though provably do not match app-specific RouteHandlerT
    const builtinRouteRegistry = new RouteRegistryImpl(); // Do NOT do withDefaultRouteRegistry() as want RouteRegistryImpl
    /*const awSnapRoute: RegisterRoutesT = <RegisterRoutesT><unknown>*/
    AwSnapRoute.registerRoute(builtinRouteRegistry);
    /*const unmountRoute = <RegisterRoutesT><unknown>*/
    UnmountRoute.registerRoute(builtinRouteRegistry);
    /*const emptyRoute: RegisterRoutesT = <RegisterRoutesT><unknown>*/
    EmptyRoute.registerRoute(builtinRouteRegistry);

    // unknown route
    if (!unknownRoute) {
      unknownRoute = builtinRouteRegistry.registerRoute(unknownRouteSpec);
    } else if (!this.regRoutesRegistry.has(unknownRoute.routeSpec.routeTemplate)) {
      // defensive
      throw new Error("Application did not register the unknown route with routeTemplate: " + unknownRoute.routeSpec.routeTemplate);
    }

    // unauthorized route
    if (!unauthorizedRoute) {
      unauthorizedRoute = builtinRouteRegistry.registerRoute(unauthorizedRouteSpec);
    } else if (!this.regRoutesRegistry.has(unauthorizedRoute.routeSpec.routeTemplate)) {
      // defensive
      throw new Error("Application did not register the unauthorized route with routeTemplate: " + unauthorizedRoute.routeSpec.routeTemplate);
    }
    return [builtinRouteRegistry, unknownRoute, unauthorizedRoute];
  }
  registerRoute(registerRoute) {
    // Can be multiple routeInfos per registerRoute (e.g., "/", "/app/home")
    const ri = registerRoute.getRouteInfo();
    if (this.routeHandlerFactoryByRouteTemplate.has(ri.routeTemplate)) {
      // Defensive: Fail fast on developer error (before any data structures for that route change)
      throw new Error(`Can not register duplicate routes for routeTemplate '${ri.routeTemplate}'`);
    }
    this.routeHandlerFactoryByRouteTemplate.set(ri.routeTemplate, ri.routeHandlerFactory);
    if (ri.routeCallback != null) {
      // register route callback with Kendo router
      this.router.addRoute(ri.routeTemplate, ri.routeCallback);
      // that.router.addRoute(ri.routeTemplate, (...args: any[]) => {
      //   LOG.debug("Invoking route callback for route " + ri.routeTemplate);
      //   ri.routeCallback.apply(args);
      // });
    }
    // else no routeCallback, then it is an internal-only 'goto' route (and IRouter doesn't know about it)

    // AbSDK Routes are backed by AbSDKRoute, which implement RegisteredRoute interface (goto, href support)
    if (isAbSDKRegisteredRoute(registerRoute)) {
      this.freezeRoute(registerRoute);
      this.addRegisteredRoute(registerRoute);
    }
  }

  /**
   * Defensive: Make absdk routes effectively immutable after they are registered,
   * as do not want apps mucking with 'live' registered routes.
   */
  freezeRoute(regRoute) {
    // tests sometimes need to be able to modify registered routes so as to 'spy' on various methods/props
    if (this.suppressFrozenRoutes) return;
    Object.freeze(regRoute.routeSpec.view);
    regRoute.routeSpec.routeParamHandler && Object.freeze(regRoute.routeSpec.routeParamHandler);
    Object.freeze(regRoute.routeSpec);
    Object.freeze(regRoute);
  }
  addRegisteredRoute(registeredRoute) {
    const ri = registeredRoute.getRouteInfo();
    const routeTemplate = ri.routeTemplate;
    if (this.getRegisteredRoute(routeTemplate)) {
      // Developer error, be defensive
      throw new Error(`Can not register duplicate AbSDK routes with routeTemplate '${routeTemplate}'`);
    }
    // Note default route '/' is represented by an additional getRouteInfo in reg route array
    this.registeredAbSDKRouteByRouteTemplate.set(routeTemplate, registeredRoute);
  }
  unregisterRoute(registeredRoute) {
    const abSDKRegRoute = registeredRoute;
    const ri = abSDKRegRoute.getRouteInfo();
    if (ri.routeTemplate === HOME_ALIAS_ROUTE.routeTemplate) {
      // Don't have any infrastructure for changing the default route (aka '/') after the router is started.
      // As no requirement to support changing the default route (for now), throw an error.
      throw new Error("Unregistering the default route '/' is not supported.");
    }
    if (ri.routeTemplate === this.defaultHomeRoute?.routeSpec.routeTemplate) {
      // The HOME_ALIAS_ROUTE '/' redirects to defaultHomeRoute, so don't allow them to unreqister the defaultHomeRoute!
      throw new Error(`Unregistering the home route '${this.defaultHomeRoute?.routeSpec.routeTemplate}' is not supported.`);
    }
    // unregister route callback with router
    this.router.removeRoute(ri.routeTemplate);
    this.routeHandlerFactoryByRouteTemplate.delete(ri.routeTemplate);
    // AbSDK Routes are backed by AbSDKRoute, which implement RegisteredRoute interface (goto, href support)
    this.registeredAbSDKRouteByRouteTemplate.delete(registeredRoute.routeSpec.routeTemplate);
  }

  /**
   * Register custom routes while the absdk router is running.
   * It is an error to invoke this method if the router is not running.
   * This API supports adding newly generated routes to a running app,
   * which prevents the need to restart the application for the new route to take effect.
   * Do not use it to register routes that are known at application startup, as otherwise bookmarks
   * may result in 'route not found' on initial application startup.
   *
   * It is an error to override an existing registered route for a given route template
   * (unregister via unregisterDynamicRoutes first).
   * @param registerRoutes
   */
  addCustomRoutes(...registerRoutes) {
    this.verifyAbSDKRouterRunningGuard("addCustomRoutes", registerRoutes);
    // Ugly cast, should be able to simplify after legacy routes (and RouteHandlerT) removed!
    // Will get an alert if attempt to override an existing route.
    this.registerRoutes(registerRoutes);
  }

  /**
   * Unregister routes while the absdk router is running.
   * It is an error to invoke this method if the router is not running.
   * It is NOT an error if the route is not currently registered (but it will log a warning).
   *
   * @param routeTemplates
   */
  removeCustomRoutes(...routeTemplates) {
    const regRoutes = [];
    routeTemplates.forEach(rt => {
      const rr = this.getRegisteredRoute(rt);
      if (rr) regRoutes.push(rr);
    });
    routeTemplates.forEach(rt => this.getRegisteredRoute(rt));
    this.verifyAbSDKRouterRunningGuard("removeCustomRoutes", regRoutes);
    this.unregisterRoutes(routeTemplates);
  }
  verifyAbSDKRouterRunningGuard(methodName, regRoutes) {
    if (!this.router.isRunning) {
      throw new Error(`Can not invoke ${methodName} before the AbSDK router is started.`);
    }
    const builtinRouteTemplates = [];
    regRoutes.forEach(rr => {
      if (!rr.routeSpec.isCustomRoute) {
        builtinRouteTemplates.push(rr.routeSpec.routeTemplate);
      }
    });
    if (builtinRouteTemplates.length > 0) {
      throw new Error(`Can not invoke ${methodName} on following routes as they are not custom routes: ${builtinRouteTemplates.join(', ')}`);
    }
  }
  createRouteHandler(routeTemplate) {
    const rhFactory = this.routeHandlerFactoryByRouteTemplate.get(routeTemplate);
    if (!rhFactory) {
      throw new Error(`There is no route handler registered for routeTemplate: '${routeTemplate}'`);
    }
    return rhFactory();
  }

  /**
   * Change to the specified newRoute in the routeRenderContext location.
   *
   * @returns {Promise<boolean>} return true if the current route was changed, and false if the
   * current route is left as is.  Most of the time the route will be changed to the desired newRoute.
   * However, it will also return true if an error workflow is triggered (such as unknown route, or unauthorized),
   * as it will 'successfully' change to the error route.
   * Callers can check the routeRenderContext.currentRouteHandler.getCurrentRoute() if they need
   * to ascertain the actual mounted newRoute identity after the changeView call.
   */
  async changeView(options) {
    const {
      newRoute: newRouteArg,
      routeRenderContext,
      addCurrentRouteToBrowserHistory,
      browserInitiated
    } = options;
    // Much easier to create UNMOUNT_ROUTE then to have newRoute support null,
    // as lots of code would need to be changed to handle it.
    const newRoute = newRouteArg ?? UnmountRoute.UNMOUNT_ROUTE;
    LOG.debug(`${browserInitiated ? "BROWSER" : "CODE"}-initiated NavigationService.changeView to route: ${newRoute.routeTemplate}`);
    if (routeRenderContext.isDestroyed) {
      // Sometimes get race conditions between unmount and RouteRenderContext destroy, especially when interactively debugging.
      LOG.warn(`Route render context '${routeRenderContext.name}' has been destroyed so ignore change to route: '${newRoute.routeTemplate}' as route render context '${routeRenderContext.name}'`);
      return false;
    }
    try {
      let routeChangeContext = this.createRouteChangeContext(newRoute, routeRenderContext, addCurrentRouteToBrowserHistory, browserInitiated);
      const routeHandlers = await this.calcUnloadAndNextRouteHandlers(routeChangeContext);
      if (routeHandlers === undefined) {
        return false; // Returns undefined when navigation is vetoed
      }
      let {
        unloadRH,
        nextRH
      } = routeHandlers;
      // As non-vetoed, try to go to nextRH, so check if authorized.
      // If auth check fails, returns nextRH pointing to unauthorized route, along with updated routeChangeContext
      // PORTAL-1010 requires using an 'Unauthorized Route' instead of displaying an unauthorized error dialog.
      // and can't go to the UnauthorizedRoute if currRH has unsaved changes (which is why the veto workflow is a prereq)
      [nextRH, routeChangeContext] = await this.authorizedCheck(nextRH, routeChangeContext);
      const successChangeToNewRoute = await this.changeToNewRoute(routeChangeContext, nextRH, unloadRH);
      return successChangeToNewRoute;
    } catch (err) {
      // CAT-1232: show err dialog w/pfs button, then navigate to AwSnap page.
      await this.logAndRenderError(err);
      // Spawn new thread for aw snap goto
      this.awSnapRoute.goto({}, {
        addCurrentRouteToBrowserHistory: true,
        routeRenderContext: options.routeRenderContext
      });
      return false; // return false as was not able to load new view
    }
  }
  async authorizedCheck(nextRH, rcc) {
    // minor route changes use same routeHandler instance, so already authorized
    if (rcc.routeChangeType !== RouteChangeType.MINOR && !nextRH.isAuthorized(rcc.newReactRoute.routeProps)) {
      const unauthorizedRoute = rcc.newReactRoute.route;
      LOG.error(`User is not authorized to navigate to route: '${unauthorizedRoute}'`);
      nextRH = await this.createRouteHandler(this.unauthorizedRoute.routeSpec.routeTemplate);
      const newUnauthorizedRoute = this.unauthorizedRoute.buildRouteFromProps({
        route: unauthorizedRoute
      });
      rcc = this.createRouteChangeContext(newUnauthorizedRoute, rcc.routeRenderContext, rcc.addToBrowserHistory, rcc.browserInitiated);
    }
    return [nextRH, rcc];
  }

  /**
   * Calculate next route handler instance, and the route handler instance to unload (if any), taking into account major
   * and minor route change reasoning (including veto support for dirty routes).
   * @param routeChangeContext
   * @return nextRH instance and unloadRH instance (if any).
   * However, if veto logic cancels navigation, it will return undefined.
   */
  async calcUnloadAndNextRouteHandlers(routeChangeContext) {
    const currRH = routeChangeContext.routeRenderContext.currentRouteHandler;
    const newRoute = routeChangeContext.newReactRoute;
    let nextRH;
    let unloadRH;
    // Four cases: no current RH (cold startup view), or major|mid|minor route change
    if (!currRH) {
      nextRH = await this.createRouteHandler(newRoute.routeTemplate);
    } else {
      // Leverage kendo veto workflow for browser-initiated vetoes as they do a nice job with Back/Forward/History when vetoed
      // Below is for remaining case of code-initiated route changes only.  Note both cases call continueToNewRoute
      // for both major and minor route changes (there are example where switching between minor views causes
      // a veto workflow, such as EI AppConf editing embedded rulesets in a popup).
      if (!routeChangeContext.browserInitiated) {
        const continueToNewRoute = await this.continueRouteChangeAsync(routeChangeContext);
        if (!continueToNewRoute) {
          LOG.info(`Route '${currRH.getCurrentRoute().route}' vetoed change to code-initiated route: ` + newRoute.route);
          return undefined;
        }
      }
      switch (routeChangeContext.routeChangeType) {
        case RouteChangeType.MAJOR:
          // if (routeChangeContext.isMajorRouteChange) {
          // Do NOT unload right here as it is possible that later mountMajorRoute call will return false,
          // which aborts changing to the new view
          unloadRH = currRH;
          nextRH = await this.createRouteHandler(newRoute.routeTemplate);
          break;
        case RouteChangeType.MID:
          unloadRH = currRH;
          nextRH = await this.createRouteHandler(newRoute.routeTemplate);
          // Copy currRH state to the nextRH instance, including callbacks and component-registered popup/destroy listeners
          // MID is only possible when both currRH and nextRH are AbSDK routes, so safe to cast
          nextRH.updateFromOldRouteHandler(currRH);
          break;
        case RouteChangeType.MINOR:
          // Minor mode change: use existing RouteHandler instance instead of creating a new one via factory.
          nextRH = currRH;
          break;
        default:
          Assert.assertNever(routeChangeContext.routeChangeType);
      }
    }
    return {
      unloadRH,
      nextRH
    };
  }

  // Past the veto stage, so we KNOW we're going to change to a new route
  async changeToNewRoute(routeChangeContext, nextRH, unloadRH) {
    // Unmount the route according to routeChangeType (noop for minor route change), and fires unmount events
    await this.unmountRoute(routeChangeContext, unloadRH);

    // WARNING: can not call nextRH.getCurrentRoute() until it has been mounted
    if (routeChangeContext.newReactRoute.routeTemplate === UnmountRoute.UNMOUNT_ROUTE_TEMPLATE) {
      // Never invoke mount lifecycle methods on an UnmountRoute!
      return true;
    }
    let continueToNewRoute = false;
    // Minor case is trivial as no unmount: do it first,
    if (routeChangeContext.routeChangeType === RouteChangeType.MINOR) {
      continueToNewRoute = await nextRH.onMinorRouteChange(routeChangeContext);
    }
    // Major or mid route change case
    else {
      // Note that unloadRH and nextRH can be the SAME route handler instance if they are using a singleton route handler!
      // As such, ensure that invoke unloadRH.unmountMajorRoute() BEFORE invoking nextRH.mountMajorRoute()...
      // Note could have two different workflows for singleton case (nextRH === unloadRH) and non-singleton case,
      // but not (yet) worth the additional complexity and maintenance of parallel workflows

      try {
        continueToNewRoute = await nextRH.mountMajorRoute(routeChangeContext);
      } catch (err) {
        // As failed to mount nextRH, unmount it so that there aren't any mem leaks
        // Detect weird singleton case for major route changes, so don't unload it again on error...
        if (nextRH !== unloadRH) {
          // Major route change to new route failed, so tear it down to be symmetric (avoid nextRH mem leaks)
          await nextRH.unmountMajorRoute();
        }
        throw err;
      }
    }
    if (continueToNewRoute) {
      // Need to update route BEFORE we setCurrentRH below as that forces an immediate rendering, and some products
      // setRouteTitle during rendering as opposed to relying on getCurrentRouteTitle.
      // If invoke updateRouteAndTitle when have old route and NEW route title, the history pushed on the back stack
      // will be wrong.
      if (routeChangeContext.routeRenderContext === this.mainAreaRouteRenderContext) {
        // On major route changes, the route has never been rendered so Hoc/RouteService callbacks
        // are not yet registered (including paramProcessors!),
        // so can't calculate route via nextRH.getCurrentRoute().route - PORTAL-1212.
        // Instead, just use incoming routeChangeContext newReactRoute, which avoid unnecessary timestamp
        // recalc. For browser-initiated route changes (incl. Back/Forward), this effectively just updates this current title
        // so that it is aligned with the new browser route (browser already updated route, so no change)

        // Note on minor/mid route changes, the current route component has already been mounted,
        // and on re-render may have already invoked 'updateRoute' change the route (e.g., remove an invalid prop
        // in ABSDK-5359), so defer to the already mounted component's calculated route value.
        const useRoute = routeChangeContext.routeChangeType === RouteChangeType.MAJOR ? routeChangeContext.newReactRoute.route : nextRH.getCurrentRoute().route;
        this.updateRouteAndTitle(useRoute, routeChangeContext.addToBrowserHistory, nextRH.getCurrentRouteTitle());
      }

      // Let the nextRH receive the newRoute state, which then triggers renderView (unless minor change, in
      // which case it is responsibility for existing UI to react to (observable) minor changes
      routeChangeContext.routeRenderContext.currentRouteHandler = nextRH;
    }
    return continueToNewRoute;
  }

  /**
   * Handles unmounting the route according to the routeChangeType.  Major route changes unmount current
   * routeHandler and route component, MID only unmounts the route handler (as route component will not be unmounted),
   * and MINOR does nothing.  It always generates an UnmountRouteEvent.
   * current route handler for routeChangeContext.
   * @param routeChangeContext
   * @param unloadRHArg  Already calculated unload Route Handler, and if undefined it defaults to
   * current route handler for routeChangeContext.
   */
  async unmountRoute(routeChangeContext, unloadRHArg) {
    // CAT-1634 : do not allow unmount exceptions to derail mounting the new route, so handle all errors
    let currentRoute = null;
    try {
      // Minor route changes do not have unloadRH defined, so grab it from routeChangeContext
      const unloadRH = unloadRHArg ?? routeChangeContext.routeRenderContext.currentRouteHandler;
      if (unloadRH == null) {
        // When starting up app, there isn't a current route at all to unmount (even though major route change),
        // so nothing to do.
        return;
      }
      currentRoute = unloadRH.getCurrentRoute().route; // used for logging

      // Calc unmount event before unloadRH changed
      const unloadReactRouteEvt = this.isRouteEventListeners() ? new UnmountRouteEvent(routeChangeContext.routeRenderContext, routeChangeContext.routeChangeType, unloadRH.getCurrentRoute()) : undefined;
      switch (routeChangeContext.routeChangeType) {
        case RouteChangeType.MAJOR:
          // Clear view before unload so that getCurrentRouteHandler never returns an unloaded RH (or get weird errors)
          routeChangeContext.routeRenderContext.currentRouteHandler = null;
        // fallthrough to MID case below to invoke unmountMajorRoute
        case RouteChangeType.MID:
          // Unload the route handler for MAJOR and MID, which allows it to clean up listeners to prevent mem leaks
          // Note for ABSDK routes, unmountMajorRoute does nothing for MID route changes
          await unloadRH.unmountMajorRoute(routeChangeContext.routeChangeType);
          break;
        case RouteChangeType.MINOR:
          // nop as neither routeHandler or route component are unmounted, only fires 'minor' unmount event
          break;
        default:
          // Provide compile error if enum not exhaustively covered by above cases
          const notReachableOrCompileError = routeChangeContext.routeChangeType;
          // runtime error if invalid routeChangeType value (including null/undefined) encountered at runtime
          /// Throws, but will be caught/wrapped by below catch so does NOT default changeRoute workflow
          throw new Error(`Unmount of route aborted as unknown RouteChangeType value: '${routeChangeContext.routeChangeType}'`);
      }

      // Fire async unmount events for the logical unmount to support 'side-effect' cleanup workflows
      if (unloadReactRouteEvt) {
        await this.routeEventListeners.callAll(unloadReactRouteEvt);
      }
    } catch (err) {
      // This is for *unexpected* errors.  Uses last-chance error renderer.
      // For better (workflow-specific) error handling, add try-catch within your route handler's unmountMajorRoute implementation
      const unmountErr = new NestedError(`Exception thrown while unmounting route: '${currentRoute}'`, err);

      // Await the error popup to finish so that background is the current view (and not the new view)
      await this.logAndRenderError(unmountErr);
    }
  }
  createRouteChangeContext(newRoute, routeRenderContext, desiredAddToBrowserHistory, browserInitiated = false, historyInitiatedFn) {
    const addCurrentRouteToBrowserHistory = /*!browserInitiated &&*/desiredAddToBrowserHistory;
    const routeChgType = this.determineRouteChangeType(routeRenderContext.currentRouteHandler, newRoute);
    const newRegRoute = this.getRegisteredRouteOrError(newRoute.routeTemplate);
    return new RouteChangeContext(newRoute, newRegRoute, routeRenderContext, browserInitiated, addCurrentRouteToBrowserHistory, routeChgType, historyInitiatedFn);
  }
  determineRouteChangeType(currRH, newRoute) {
    const routeChgType = !currRH ? RouteChangeType.MAJOR : currRH.getRouteChangeType(newRoute);
    return routeChgType;
  }

  /**
   * Recalculate the route and route title for the current area (including route args).
   * Only updates browser URL/history if the given routeHandler is the current main area route handler (ABSDK-5302).
   * Secondary routes do not own the browser URL, so their updateRoute calls must not touch browser history.
   *
   * @param routeHandler the route handler requesting the update
   * @param {boolean} addToBrowserHistory if true the new route (url with route) will be added to the browser history
   * in order to support browser back/forward navigation.
   * If addToBrowserHistory is false, the route will still be updated but it won't add a new entry in the the
   * browser history.
   */
  updateCurrentViewRoute(routeHandler, addToBrowserHistory) {
    const mainAreaRH = this.currentMainAreaRouteHandler;
    if (mainAreaRH && mainAreaRH === routeHandler) {
      this.updateRouteAndTitle(mainAreaRH.getCurrentRoute().route, addToBrowserHistory, mainAreaRH.getCurrentRouteTitle());
    }
  }

  /**
   * @returns A ReactRoute representing the currently active route for the mainAreaRouteRenderContext,
   * or undefined if there is no mounted route.
   */
  getCurrentRouteInfo() {
    const reactRoute = this.currentMainAreaRouteHandler?.getCurrentRoute();
    return reactRoute;
  }
  updateRouteAndTitle(route, addToBrowserHistory, routeTitle) {
    this.updateRoute(route, addToBrowserHistory);
    if (routeTitle != null) {
      this.setRouteTitle(routeTitle);
    }
  }

  /**
   * This will bookmark the current URL (including the CURRENT doc title) before changing route. So if you
   * are calling it in conjunction with a change in doc title for the new URL, change the doc title AFTER
   * calling this.  Note that it is a no-op if the newRoute is the same as the current route.
   * @param newRoute
   * @param addToBrowserHistory
   */
  updateRoute(newRoute, addToBrowserHistory) {
    // router logs bookmark and replace, so only output to console
    if (addToBrowserHistory) {
      console.debug(`updateRoute bookmarked ${window.location.href} while navigating to: ${newRoute}`);
      this.router.bookmark(newRoute);
    } else {
      console.debug(`updateRoute nohistory while navigating to: ${newRoute}`); //`
      this.router.replace(newRoute);
    }
  }

  /**
   * Set the route title for the current view, which will be displayed on the browser tab, and as part of the
   * browser navigation history (including browser history displayed on long browser back/forward button presses)
   * <p>
   * Note that the route url may be updated (e.g., /rulesetId=43...) first, then after async rpc fetching
   * we can generate a pretty route title (e.g., "Ruleset CreditScore" that corresponds to ruleset obtained
   * from fetched ruleset 43) As such, we typically set the route title as part of rendering, where you'd 'render'
   * the route title as soon as the route title argument values became available.
   *
   * @param docTitle localized page title
   */
  setRouteTitle(docTitle) {
    // Yes, devs could directly call BrowserUtils.setDocumentTitle if they know about it.
    // However, want NavigationService to be one-stop shopping for developers for their route-related needs,
    // so add it as an explicit method (developer api clarity outweighs minor additional runtime overhead)

    // ABSDK-373: title should be: routeSpecificTitle + ' - ' + productName
    const fullTitle = docTitle + (docTitle ? " - " : "") + AbWebServices.sessionData.productInfo.name;
    BrowserUtils.setDocumentTitle(fullTitle);
  }

  /// RTD Route implementation of BuiltinRouteProvider ////

  gotoHome() {
    // Will invoke whichever app route registered itself as the default route (registered with '/')
    // Note '/' is typically a 'RootAliasRoute' that simply redirects to the 'real' home route via a 'goto'.
    // If the navigationService was called with the 'home route' arg, then it will go directly to it,
    // which avoids the 'redirect' overhead of the RootAliasRoot.
    return this.changeView({
      newRoute: this.homeRoute,
      routeRenderContext: this.mainAreaRouteRenderContext,
      addCurrentRouteToBrowserHistory: true
    });
  }

  /** @returns true iff there are any dirty routes */
  hasUnsavedChanges() {
    return this.routeRenderContextMgr.hasUnsavedChanges();
  }

  /**
   * Log out, waiting for dirty routes to be optionally saved
   * @param force   Skips the dirty route check. This is used for the
   *                Express>It user inactivity timeout, where letting a page
   *                put up a save/discard message would prevent the logout
   *                from happening without this flag.
   */
  async gotoLogout(force = false) {
    if (!force) {
      const noDirtyRoutes = await this.continueAfterNoDirtyRoutes();
      if (!noDirtyRoutes) {
        // above continueAfterNoDirtyRoutes has cancelled instead of save|discard, so still unsaved changes.
        // On cancel, stop the logout workflow (user can manually address)
        return false;
      }
    }

    // This logout() method never resolves before browser tab is reloaded, so will never get to return statement
    await AbWebServices.authenticationService.logout();
    // Just in case, return promise that never resolves
    return new Promise(() => {});
  }

  /**
   * Note this will show the 'veto workflow' for every dirty route.  In theory, this could be a
   * very problematic user experience (multiple dialogs with insufficient context as to what areas
   * of the screen they are talking about being dirty).  However, right now Portal and MHub are agreeing that
   * they will have at most one dirty route at any give time (via React.Context Edit context).
   *
   * Future Enhancement: if there can be multiple dirty routes in an app, then we are going to have to improve the
   * user experience.  Perhaps a single level dialog with 'SaveAll/DiscardAll/Cancel' buttons,
   * which would entail that save/discard are directly supported by the IRouteHandler interface?
   */
  async continueAfterNoDirtyRoutes(/*retryWorkflowFn: () => Promise<boolean>*/
  ) {
    // Note that we are NOT unloading the underlying dirty routes,
    // but just invoking their veto workflows to allow them to save/discard their changes,
    // or abort this workflow entirely

    const dirtyRCCs = this.routeRenderContextMgr.getDirtyRouteRenderContexts();
    for (let dirtyRRC of dirtyRCCs) {
      const routeChangeContext = new RouteChangeContext(UnmountRoute.UNMOUNT_ROUTE, this.unmountRegRoute, dirtyRRC, false, false, RouteChangeType.MAJOR);
      const dirtyCurrRH = dirtyRRC.currentRouteHandler;
      // Note this can pop up a veto dialog synchronously and return false.  If the user does an action
      // that results in the route being clean (e.g., save or discard), the veto dialog
      // will call the retryWorkflowFn to restart the workflow from the start
      // (and this time the route will no longer be dirty) - poor man's continuation (but simple, thorough,
      // and highly likely that there is only dirty route, so fast enough).
      let {
        continueRC,
        retryRC
      } = this.continueRouteChangeSync(dirtyCurrRH, routeChangeContext);
      if (!continueRC && retryRC) {
        // support save/discard/cancel dialog prompt workflow
        continueRC = await dirtyCurrRH.retryRouteChange(routeChangeContext);
      }
      if (!continueRC) {
        // Abort as user cancelled instead of saving/discarding the unsaved changes (veto workflow)
        return false;
      }
    }
    return true;
  }

  /**
   * Retrieves RegisteredRoute for routeTemplate.  The underlying route must be an absdk route.
   * It will throw an error if there is no RegisteredRoute for a given routeTemplate.
   */
  getRegisteredRouteOrError(routeTemplate) {
    const regRoute = this.getRegisteredRoute(routeTemplate);
    if (!regRoute) throw new Error(`No AbSDKRoute is registered for routeTemplate='${routeTemplate}'`);
    return regRoute;
  }
  getRegisteredRoute(routeTemplate) {
    const regRoute = this.registeredAbSDKRouteByRouteTemplate.get(routeTemplate);
    return regRoute;
  }
  getAllRegisteredRoutes() {
    return Array.from(this.registeredAbSDKRouteByRouteTemplate.values());
  }

  // Route Event Support
  isRouteEventListeners() {
    return this.routeEventListeners.listenerCount > 0;
  }

  /**
   * Register for all route events in all RouteRenderContext areas.
   * Most callers are better off directly registering with their RouteRenderContext
   * instead to ensure that they only receive events for that RouteRenderContext
   */
  addCallback(listener) {
    return this.routeEventListeners.addCallback(listener);
  }
  removeCallback(listener) {
    this.routeEventListeners.removeCallback(listener);
  }
}
_NavigationService = NavigationService;
// Use observable.box to make static members observable.
// The AbRouteMainAreaRender will re-render when router initialized (portal displays before router initialized)
_defineProperty(NavigationService, "SINGLETON", observable.box(undefined));
_defineProperty(NavigationService, "INSTANCE_ID", 1);
//# sourceMappingURL=NavigationService.js.map
